import {
  Directive,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, map } from 'rxjs';

import { ClickProtection } from '../click-protection/click-protection';
import { SafeClickDirective } from '../safe-click/safe-click.directive';
import { CONFIRM_CLICK_CONFIG, ConfirmClickConfig } from './confirm-click.config';
import { ConfirmDialogComponent } from './confirm-dialog.component';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[confirmClick]',
  standalone: true,
})
export class ConfirmClickDirective implements OnInit, OnDestroy {
  /** Custom message. Falls back to the config token value. */
  @Input('confirmClick') message?: string;

  /** Custom title. Falls back to the config token value. */
  @Input() confirmTitle?: string;

  /** Confirm button color. Falls back to the config token value. */
  @Input() confirmColor?: ConfirmClickConfig['confirmColor'];

  /** Throttle window in ms. Only applies when safeClick is not present. */
  @Input() confirmSafeTime = 250;

  readonly #elRef: ElementRef<HTMLElement>;
  readonly #dialog: MatDialog;
  readonly #config: ConfirmClickConfig;
  readonly #safeClick: SafeClickDirective | null;

  #clickProtection?: ClickProtection<MouseEvent>;
  #unsubscribe?: () => void;
  #waitingConfirmation = false;

  constructor(
    elRef: ElementRef<HTMLElement>,
    dialog: MatDialog,
    @Inject(CONFIRM_CLICK_CONFIG) config: ConfirmClickConfig,
    @Optional() safeClick: SafeClickDirective
  ) {
    this.#elRef = elRef;
    this.#dialog = dialog;
    this.#config = config;
    this.#safeClick = safeClick ?? null;
  }

  ngOnInit(): void {
    if (this.#safeClick) {
      // safeClick handles interception and throttling.
      // Hook into pre-dispatch: safeClick re-dispatches only if the user confirms.
      this.#safeClick.setPreDispatchHook(() => this._openDialog());
    } else {
      this.#clickProtection = new ClickProtection<MouseEvent>({
        safeTime: this.confirmSafeTime,
        context: ConfirmClickDirective.name,
      });

      this.#unsubscribe = this.#clickProtection.onValidClick((event) => {
        this._openDialog().subscribe((confirmed) => {
          if (confirmed) {
            this.#waitingConfirmation = true;
            this._dispatchClick(event);
          }
        });
      });

      this.#elRef.nativeElement.addEventListener('click', this._interceptClick, true);
    }
  }

  ngOnDestroy(): void {
    if (this.#safeClick) {
      this.#safeClick.setPreDispatchHook(null);
    } else {
      this.#unsubscribe?.();
      this.#clickProtection?.destroy();
      this.#elRef.nativeElement.removeEventListener('click', this._interceptClick, true);
    }
  }

  private _interceptClick = (event: MouseEvent): void => {
    if (this.#waitingConfirmation) {
      this.#waitingConfirmation = false;
      return;
    }

    event.stopImmediatePropagation();
    event.preventDefault();

    this.#clickProtection?.click(event);
  };

  private _openDialog(): Observable<boolean> {
    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      data: {
        ...this.#config,
        ...(this.confirmTitle ? { title: this.confirmTitle } : {}),
        message: this.message ?? this.#config.message,
        ...(this.confirmColor ? { confirmColor: this.confirmColor } : {}),
      } satisfies ConfirmClickConfig,
      disableClose: true,
    });

    return dialogRef.afterClosed().pipe(map((result) => !!result));
  }

  private _dispatchClick(originalEvent: MouseEvent): void {
    const newEvent = new MouseEvent('click', {
      bubbles: originalEvent.bubbles,
      cancelable: originalEvent.cancelable,
      view: originalEvent.view,
      detail: originalEvent.detail,
      screenX: originalEvent.screenX,
      screenY: originalEvent.screenY,
      clientX: originalEvent.clientX,
      clientY: originalEvent.clientY,
      ctrlKey: originalEvent.ctrlKey,
      altKey: originalEvent.altKey,
      shiftKey: originalEvent.shiftKey,
      metaKey: originalEvent.metaKey,
      button: originalEvent.button,
      buttons: originalEvent.buttons,
    });
    this.#elRef.nativeElement.dispatchEvent(newEvent);
  }
}
